import React, { useState, useEffect, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import GuestHeader from "./guest-header";
import { debounce } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../Utils/toastContext";
import { subDays, subMonths, subYears, format } from "date-fns";
import { Chart } from "chart.js";

const PortfolioChartSection = ({ displayName = "", loading = false }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [selectedFilter, setSelectedFilter] = useState("5Y");
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [stockPrices, setStockprices] = useState([]);
  const [combineIndex, setCombineIndex] = useState([]);
  const [rawData, setrawData] = useState([]);

  const generateGraphData = (filter) => {
    const today = new Date();
    let datePoints = [];

    if (filter === "1M") {
      for (let i = 30; i >= 0; i -= 3) {
        datePoints.push(subDays(today, i));
      }
    } else if (filter === "3M") {
      for (let i = 90; i >= 0; i -= 7) {
        datePoints.push(subDays(today, i));
      }
    } else if (filter === "1Y") {
      for (let i = 12; i >= 0; i--) {
        datePoints.push(subMonths(today, i));
      }
    } else if (filter === "5Y") {
      for (let i = 5; i >= 0; i--) {
        datePoints.push(subYears(today, i));
      }
    }

    const formattedData = datePoints.map((date) => ({
      x: format(date, "yyyy-MM-dd"),
      y: Math.floor(Math.random() * 30) + 10,
    }));

    const formattedCaprockData = datePoints.map((date) => ({
      x: format(date, "yyyy-MM-dd"),
      y: Math.floor(Math.random() * 30) + 5,
    }));

    const formattedBenchmarkData = datePoints.map((date) => ({
      x: format(date, "yyyy-MM-dd"),
      y: 15, // static benchmark value or customize as needed
    }));

    return {
      labels: datePoints.map((date) => format(date, "yyyy-MM-dd")),
      datasets: [
        {
          label: "Your Strategy",
          data: formattedData,
          borderColor: "#DC4646",
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#DC4646",
          fill: true,
        },
        {
          label: "Caprock Strategy",
          data: formattedCaprockData,
          borderColor: "#3047EC",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#3047EC",
          fill: true,
        },
        {
          label: "Benchmark",
          data: formattedBenchmarkData,
          borderColor: "#34C046",
          backgroundColor: "rgba(34, 197, 94, 0.05)",
          borderDash: [10, 10],
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        },
      ],
    };
  };

  const labelsLine =
    rawData.length > 0
      ? rawData.map((item) => {
          const date = new Date(item.date); // Parse the date
          return date; // Return Date object (Chart.js can parse Date objects)
        })
      : [];
  const optionsLine = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide the legend
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#111827",
        titleColor: "#fff",
        borderColor: "#374151",
        borderWidth: 1,
        padding: 12,
        displayColors: false, // Hide colored dots
        callbacks: {
          label: function (tooltipItem) {
            const datasetLabel = tooltipItem.dataset.label || "";
            const value = tooltipItem.formattedValue;
            return `${datasetLabel}: ${value}%`;
          },
          labelTextColor: function (context) {
            // Set the text color per dataset
            const index = context.datasetIndex;
            return ["#DC4646", "#3047EC", "#34C046"][index] || "#d1d5db";
          },
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "day"
              : selectedFilter === "1Y"
              ? "month"
              : "year",
          tooltipFormat:
            selectedFilter === "1M" || selectedFilter === "3M"
              ? "dd MMM yyyy"
              : selectedFilter === "1Y"
              ? "MMM yyyy"
              : "yyyy",
          displayFormats: {
            day: "dd MMM",
            month: "MMM yyyy",
            year: "yyyy",
          },
        },
        ticks: {
          color: "black",
          maxTicksLimit:
            selectedFilter === "1M"
              ? 10
              : selectedFilter === "3M"
              ? 6
              : selectedFilter === "1Y"
              ? 6
              : 5,
          callback: function (value) {
            const date = new Date(value);
            if (selectedFilter === "1M" || selectedFilter === "3M") {
              return `${date
                .getDate()
                .toString()
                .padStart(2, "0")} ${date.toLocaleString("en-US", {
                month: "short",
              })}`;
            } else if (selectedFilter === "1Y") {
              return `${date.toLocaleString("en-US", {
                month: "short",
              })} ${date.getFullYear()}`;
            } else {
              return date.getFullYear();
            }
          },
          font: {
            size: 12,
          },
        },
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        border: {
          color: "#000",
        },
        afterFit: function (scale) {
          scale.height = 50;
        },
      },

      y: {
        ticks: {
          color: "black", // Set y-axis label color to black
          font: {
            size: 12,
          },
          callback: function (value) {
            return value + "%"; // Optional: format y-axis label
          },
        },
        grid: {
          color: "#e5e7eb",
          borderDash: [4, 4],
        },
      },
    },
  };

  const dataLine = generateGraphData(selectedFilter);

  const postFetchHistory = async () => {
    const token = localStorage.getItem("jwtToken");
    const apiUrl = `${BASE_URL}/v1/investment-portfolio/simulated-historic-performance`;
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const bodyData = {
      portfolio_id: 109,
      time_interval: selectedFilter,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));

        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };
  const fetchHistoryViewData = useCallback(
    debounce(async (e) => {
      setIsLoadingGraph(true);
      let toastMessage = null;
      try {
        const response = await postFetchHistory();
        if (response.status === 200) {
          setrawData(response.data.performance_data);
          const stockPrices = response.data.performance_data.map(
            (item) => item.value
          );
          const combineIndices = response.data.combined_index_graph.map(
            (item) => item.value
          );
          setCombineIndex(combineIndices);
          setStockprices(stockPrices);
        }
      } catch (error) {
        toastMessage = { message: error.message, type: "error" };
      } finally {
        // Show toast after loading is false
        setIsLoadingGraph(false);
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [selectedFilter] // Dependencies
  );

  useEffect(() => {
    fetchHistoryViewData();
  }, [selectedFilter]);
  return (
    <div className="px-4 py-2">
      <GuestHeader displayName={displayName} />

      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div className="bg-white shadow-xl rounded-xl py-4 px-10 sm:px-10 mt-4 space-y-6 transition-all duration-300 ease-in-out">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-medium text-black mb-1">
              Caprock Model Portfolio - Caprock Funds Management Pty Ltd
              Multi-Asset SMA Aggressive (100/0)
            </h2>
            <p className="text-sm text-gray-500">
              How your portfolio has performed vs. our optimized strategy
            </p>
          </div>
        </div>

        <div className=" bg-white">
          <div className="mb-4 flex flex-wrap justify-between items-center">
            <span>
              <h3 className="text-lg font-semibold text-black w-full md:w-auto">
                Performance Analysis
              </h3>
            </span>
          </div>

          <div className="mb-4 mt-4 flex w-full justify-between flex-wrap">
            <div className="pr-4 flex-wrap items-center">
              <div className="flex">
                {["5Y", "1Y", "3M", "1M"].map((filter, index) => (
                  <button
                    key={filter}
                    className={`px-3 py-1 shadow-md ${
                      filter === "1M" ? "rounded-r-xl" : ""
                    }
            ${filter === "5Y" ? "rounded-l-xl" : ""}
             
               ${
                 selectedFilter === filter
                   ? "bg-black text-white"
                   : "bg-white text-black"
               }`}
                    onClick={() => setSelectedFilter(filter)}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
            {/* Flex container for image and performance text */}
            <div className="flex flex-col mt-4 md:mt-0   md:flex-row items-start ml-auto space-x-0 space-y-0">
              <div className="flex items-center pl-2 md:pl-0">
                <img src="/green-benchmark.svg" alt="taa" className="mr-1" />
                <span className="text-xs text-black">Benchmark</span>
              </div>
              <div className="flex items-center pl-2">
                <img src="/blue-taa.svg" alt="taa" className="mr-1" />
                <span className="text-xs text-black">Caprock Strategy</span>
              </div>
              <div className="flex items-center pl-2">
                <img src="/red-saa.svg" alt="income-orange" className="mr-1" />
                <span className="text-xs text-black">Your Strategy</span>
              </div>
            </div>
          </div>
          {isLoadingGraph ? (
            <div className="shimmer-container-list mb-4">
              {/* Creating 5 rows of shimmer effect */}
              {[...Array(2)].map((_, rowIndex) => (
                <div key={rowIndex} className="shimmer-row-list">
                  {/* Creating 7 cells in each row for shimmer */}
                  {[...Array(2)].map((_, colIndex) => (
                    <div key={colIndex} className="shimmer-cell-list h-20">
                      <div className="shimmer-loader"></div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ width: "100%", height: "400px" }}>
              <Line data={dataLine} options={optionsLine} />
            </div>
          )}
        </div>
        {/* Summary */}
        <div className="bg-white rounded-2xl shadow-sm px-4 text-gray-800">
          <h2 className="text-base sm:text-lg font-semibold mb-2 text-black">
            Performance Overview
          </h2>
          <p className="text-sm  leading-relaxed">
            Your portfolio achieved an average annual return of{" "}
            <strong className="text-[#DC4646] font-medium">7.0%</strong> over
            the past five years. By comparison, our strategic model demonstrated
            a projected annual growth of{" "}
            <strong className="text-[#3047EC] font-medium">11.4%</strong> during
            the same period.
          </p>
        </div>

        {/* CTA Onboarding Section */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 shadow-lg border border-blue-100">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-md sm:text-xl font-semibold text-black mb-1">
                Ready to Invest in This Strategy?
              </h3>
              <p className="text-sm text-gray-600 max-w-lg">
                To begin investing in the Caprock Model Portfolio, you need to
                be onboarded with us. Join now and take the next step toward
                optimizing your returns.
              </p>
            </div>
            <button
              className="text-sm mt-3 sm:mt-0 px-6 py-2 rounded-full bg-[#005A9C] text-white font-semibold hover:bg-[#004680] transition duration-200"
              onClick={() => navigate("/onboarding")} // Adjust navigation as needed
            >
              Get Onboarded
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioChartSection;
